import type {
  ArgsOf,
  RequestOf,
  StagedRequestOf,
} from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";

import { commonAllAccess } from "./all.ts";
import { commonGroupAccess, commonSshAccess } from "./group.ts";
import {
  groupNameParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";

const parentLabel = "GCP project";

export const GcloudSshAccesses = {
  session: Access({
    ...commonSshAccess,
    maturity: "ga",
    delegation: {},
    generated: {
      roles: {
        type: "object",
        properties: {
          role: { type: "string" },
          permissionId: { type: "string" },
        },
        multivalued: true,
      },
    }, // depends on the ssh access subtype
    parameters: {
      provider: { ...requiredProviderParameter, choices: ["gcloud"] as const },
      group: groupNameParameter,
      parent: parentParameter(parentLabel),
      destination: sessionDestinationParameter,
      sudo: sudoParameter,
      region: regionParameter,
      zone: zoneParameter,
      publicKey: publicKeyParameter,
      resource: {
        type: "object",
        label: "Resource specific data",
        properties: {
          fullName: { type: "generated", label: "Full name" },
          projectId: { type: "generated", label: "Project ID" },
          instanceName: { type: "generated", label: "Instance name" },
          zone: { type: "generated", label: "Zone" },
          tagKey: { type: "generated", label: "Tag key", optional: true },
          tagValue: { type: "generated", label: "Tag value", optional: true },
          permissionList: {
            type: "object",
            label: "Permission list",
            // TODO: add type safety to this object after moving gcloud to lifecycle SDK.
            properties: {},
            multivalued: true,
          },
        },
      },
    },
    render: {
      locator: "parent",
      policy: "sudo",
      resource: "destination",
    } as const,
  }),
  group: commonGroupAccess(parentLabel),
  all: commonAllAccess,
  parent: commonParentAccess(parentLabel),
} satisfies EmptyAccesses;

export type GcloudSshAccesses = typeof GcloudSshAccesses;
export type GcloudSshAccess = GcloudSshAccesses[keyof GcloudSshAccesses];
export type GcloudSshAccessArgs = ArgsOf<GcloudSshAccess>;

export type GcloudSshSessionAccess = GcloudSshAccesses["session"];
export type GcloudSshSessionAccessArgs = ArgsOf<GcloudSshSessionAccess>;
export type GcloudSshSessionRequest = RequestOf<GcloudSshSessionAccess>;
export type StagedGcloudSshSessionRequest =
  StagedRequestOf<GcloudSshSessionAccess>;

export type GcloudSshGroupAccess = GcloudSshAccesses["group"];
export type GcloudSshGroupAccessArgs = ArgsOf<GcloudSshGroupAccess>;

export type GcloudSshParentAccess = GcloudSshAccesses["parent"];
export type GcloudSshParentAccessArgs = ArgsOf<GcloudSshParentAccess>;

export type GcloudSshAllAccess = GcloudSshAccesses["all"];
export type GcloudSshAllAccessArgs = ArgsOf<GcloudSshAllAccess>;
